import {
  PROJECTION_METHOD_LABELS,
  TRAFFIC_LIGHT_LABELS,
  formatDecimal,
  longTermSchema,
  projectionsSchema,
  targetBatchResultSchema,
  targetValueSchema,
  type LongTerm,
  type ProjectionItem,
} from '@fluxus/shared';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarRange, FolderKanban, Pencil, TrendingUp } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { moneyInput } from '@/lib/money';

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const value = (v: number | null) => (v === null ? '—' : formatDecimal(v, 2));
const inputText = (v: number | null | undefined) =>
  v === null || v === undefined ? '' : v.toLocaleString('pt-BR', { maximumFractionDigits: 6 });
const textareaClass =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring aria-invalid:border-danger';

/** Página com o projeto escolhido (carregando, erro e "sem projeto" iguais nas duas telas). */
function ProjectPage({
  title,
  description,
  emptyDescription,
  children,
}: {
  title: string;
  description: string;
  emptyDescription: string;
  children: (p: { organizationId: string; projectId: string; readOnly: boolean }) => React.ReactNode;
}) {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  return (
    <PageContainer className="max-w-6xl">
      <PageHeader title={title} description={description} />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description={emptyDescription}
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="mt-4">
            <ProjectPicker projects={projects} project={project} onSelect={select} />
          </div>
          <div key={project.id}>
            {children({ organizationId, projectId: project.id, readOnly: project.status !== 'ACTIVE' })}
          </div>
        </>
      )}
    </PageContainer>
  );
}

/**
 * Planejamento › Longo prazo (T-181b, RF-29, PRD §11.17; DEC-064): a tabela ano × indicadores com as metas do ano
 * em todo o horizonte do projeto (e o realizado), editável por quem edita metas.
 */
export function LongoPrazoPage() {
  return (
    <ProjectPage
      title="Longo prazo"
      description="Metas de cada ano do horizonte por indicador (ano × indicadores) e o realizado, para planejar além do ano corrente."
      emptyDescription="As metas plurianuais pertencem a um projeto. Quando a empresa tiver um projeto em andamento, elas aparecem aqui."
    >
      {(p) => <LongTermSection {...p} />}
    </ProjectPage>
  );
}

function LongTermSection({
  organizationId,
  projectId,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const can = useCan();
  const queryClient = useQueryClient();
  const project = `/organizations/${organizationId}/projects/${projectId}`;
  const query = useQuery({
    queryKey: ['long-term', projectId],
    queryFn: ({ signal }) => client.request(`${project}/long-term`, { schema: longTermSchema, signal }),
  });
  const [chosen, setChosen] = useState<string[] | null>(null);
  const [editing, setEditing] = useState(false);
  const canEdit = !readOnly && can('metas-resultados', 'edit');

  if (query.isPending)
    return <LoadingState className="mt-6" rows={4} label="Carregando as metas plurianuais" />;
  if (query.isError) {
    return (
      <ErrorState
        className="mt-6"
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }
  const data = query.data;
  if (!data.kpis.length) {
    return (
      <EmptyState
        className="mt-6 rounded-lg border border-dashed border-border py-12"
        icon={CalendarRange}
        title="Nenhum indicador no seu escopo"
        description="Cadastre os indicadores do projeto (ou peça acesso à área deles): a tabela mostra as metas de cada ano do horizonte."
      />
    );
  }
  const shown = data.kpis.filter((k) => (chosen ?? data.kpis.map((x) => x.id)).includes(k.id));
  return (
    <section aria-labelledby="long-term-title" className="mt-6 flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="long-term-title" className="text-lg font-bold">
            Ano × indicadores
          </h2>
          <p className="text-sm text-muted-foreground">
            Horizonte {data.years[0]}–{data.years.at(-1)}. Meta do ano: a cadastrada como "Ano" ou, sem ela, o
            acumulado das metas dos períodos. O horizonte se ajusta no cadastro do projeto.
          </p>
        </div>
        {canEdit && !editing ? (
          <Button variant="outline" onClick={() => setEditing(true)}>
            <Pencil aria-hidden />
            Editar metas
          </Button>
        ) : null}
      </div>
      <fieldset className="flex flex-wrap gap-x-4 gap-y-1">
        <legend className="mb-1 text-sm font-medium">Indicadores na tabela</legend>
        {data.kpis.map((k) => {
          const on = shown.some((s) => s.id === k.id);
          return (
            <label key={k.id} className="flex min-h-11 items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-brand"
                checked={on}
                disabled={on && shown.length === 1}
                onChange={() =>
                  setChosen(
                    on
                      ? shown.filter((s) => s.id !== k.id).map((s) => s.id)
                      : [...shown.map((s) => s.id), k.id],
                  )
                }
              />
              {k.code} — {k.name}
            </label>
          );
        })}
      </fieldset>
      {editing ? (
        <LongTermEditor
          project={project}
          data={data}
          kpis={shown}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            await queryClient.invalidateQueries({ queryKey: ['long-term', projectId] });
          }}
        />
      ) : (
        <LongTermTable data={data} kpis={shown} />
      )}
    </section>
  );
}

function LongTermTable({ data, kpis }: { data: LongTerm; kpis: LongTerm['kpis'] }) {
  const cell = (kpiId: string, year: number) => data.cells.find((c) => c.kpiId === kpiId && c.year === year);
  return (
    <div
      role="region"
      aria-label="Tabela ano × indicadores"
      tabIndex={0}
      className="relative overflow-x-auto rounded-lg border border-border"
    >
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">
          Metas do ano por indicador em cada ano do horizonte, com o realizado
        </caption>
        <thead className="bg-muted/60 text-xs text-muted-foreground">
          <tr>
            <th scope="col" className="sticky left-0 bg-muted px-3 py-2 text-left font-semibold">
              Ano
            </th>
            {kpis.map((k) => (
              <th key={k.id} scope="col" className="min-w-36 px-3 py-2 text-right font-semibold">
                {k.name}
                <span className="block font-normal">
                  {k.code} · {k.unitLabel}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.years.map((year) => (
            <tr key={year} className="border-t border-border">
              <th scope="row" className="sticky left-0 bg-surface px-3 py-2 text-left font-semibold tabular">
                {year}
                {year === data.currentYear ? (
                  <span className="block text-xs font-normal text-muted-foreground">ano corrente</span>
                ) : year > data.currentYear ? (
                  <span className="block text-xs font-normal text-muted-foreground">futuro</span>
                ) : null}
              </th>
              {kpis.map((k) => {
                const c = cell(k.id, year);
                return (
                  <td key={k.id} className="px-3 py-2 text-right tabular">
                    <span className="font-semibold">{value(c?.target ?? null)}</span>
                    {c?.targetSource === 'PERIODS' ? (
                      <span className="block text-xs text-muted-foreground">acumulado dos períodos</span>
                    ) : null}
                    {c?.yearTarget && c.yearTarget.approvalStatus !== 'APPROVED' ? (
                      <span className="block text-xs font-medium text-status-yellow-fg">
                        {c.yearTarget.approvalStatus === 'PENDING' ? 'pendente de aprovação' : 'rejeitada'}:{' '}
                        {value(c.yearTarget.value)}
                      </span>
                    ) : null}
                    {c?.actual !== null && c?.actual !== undefined ? (
                      <span className="block text-xs text-muted-foreground">
                        realizado {value(c.actual)} até {MONTHS[(c.lastActualMonth ?? 1) - 1]}
                      </span>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LongTermEditor({
  project,
  data,
  kpis,
  onClose,
  onSaved,
}: {
  project: string;
  data: LongTerm;
  kpis: LongTerm['kpis'];
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const ids = useId();
  // Rascunho por KPI e ano: começa na versão vigente da meta "Ano" (inclusive pendente); sem ela, em branco
  // (o acumulado dos períodos aparece só como dica).
  const initial = (kpiId: string, year: number) =>
    inputText(data.cells.find((x) => x.kpiId === kpiId && x.year === year)?.yearTarget?.value);
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      kpis.flatMap((k) => data.years.map((y) => [`${k.id}|${y}`, initial(k.id, y)] as const)),
    ),
  );
  const [reason, setReason] = useState('');
  const [needsReason, setNeedsReason] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    const cells: { kpiId: string; year: number; value: number | null }[] = [];
    for (const k of kpis) {
      for (const year of data.years) {
        const key = `${k.id}|${year}`;
        const text = (draft[key] ?? '').trim();
        if (text === initial(k.id, year).trim()) continue;
        if (text === '') {
          cells.push({ kpiId: k.id, year, value: null });
          continue;
        }
        const parsed = targetValueSchema.safeParse(moneyInput(text));
        if (!parsed.success) {
          setError(new Error(`${k.name} em ${year}: ${parsed.error.issues[0]?.message ?? 'valor inválido'}`));
          return;
        }
        cells.push({ kpiId: k.id, year, value: parsed.data });
      }
    }
    if (!cells.length) {
      setError(new Error('Nenhuma meta mudou.'));
      return;
    }
    if (needsReason && !reason.trim()) {
      setError(new Error('Justificativa obrigatória.'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const out = await client.request(`${project}/targets`, {
        method: 'PUT',
        body: {
          cells: cells.map((c) => ({
            kpiId: c.kpiId,
            periodType: 'YEAR',
            year: c.year,
            period: 1,
            businessUnitId: null,
            departmentId: null,
            value: c.value,
          })),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
        schema: targetBatchResultSchema,
      });
      toast.success('Metas plurianuais salvas', {
        description: `${out.created} criadas, ${out.revised} revisadas, ${out.removed} removidas.${
          out.pendingApproval ? ' Ficaram pendentes de aprovação (valem depois de aprovadas).' : ''
        }`,
      });
      await onSaved();
    } catch (err) {
      // Mudar ou remover meta já definida (ou de período fechado) exige justificativa: a tela pede.
      if (err instanceof ApiError && (err.code === 'REASON_REQUIRED' || err.code === 'CLOSED_PERIOD')) {
        setNeedsReason(true);
      }
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form noValidate onSubmit={save} className="flex flex-col gap-4">
      <div
        role="region"
        aria-label="Editar metas do ano"
        tabIndex={0}
        className="relative overflow-x-auto rounded-lg border border-border"
      >
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">Meta do ano por indicador em cada ano do horizonte</caption>
          <thead className="bg-muted/60 text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="sticky left-0 bg-muted px-3 py-2 text-left font-semibold">
                Ano
              </th>
              {kpis.map((k) => (
                <th key={k.id} scope="col" className="min-w-40 px-3 py-2 text-right font-semibold">
                  {k.name} ({k.unitLabel})
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.years.map((year) => (
              <tr key={year} className="border-t border-border">
                <th
                  scope="row"
                  className="sticky left-0 bg-surface px-3 py-2 text-left font-semibold tabular"
                >
                  {year}
                </th>
                {kpis.map((k) => {
                  const key = `${k.id}|${year}`;
                  const c = data.cells.find((x) => x.kpiId === k.id && x.year === year);
                  // O campo começa na versão vigente da meta "Ano" (é ela que se revisa); pendente ou rejeitada,
                  // a casa diz o estado e a meta que vale hoje.
                  const status = c?.yearTarget?.approvalStatus;
                  const hint =
                    status === 'PENDING' || status === 'REJECTED'
                      ? `${status === 'PENDING' ? 'Pendente de aprovação' : 'Rejeitada'} · vale hoje ${value(c?.target ?? null)}`
                      : null;
                  return (
                    <td key={k.id} className="px-3 py-2 align-top">
                      <Input
                        id={`${ids}-${key}`}
                        aria-label={`${k.name} em ${year}`}
                        aria-describedby={hint ? `${ids}-${key}-hint` : undefined}
                        inputMode="decimal"
                        disabled={saving}
                        value={draft[key] ?? ''}
                        placeholder={c?.targetSource === 'PERIODS' ? inputText(c.target) : ''}
                        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                        className="h-11 text-right tabular"
                      />
                      {hint ? (
                        <span
                          id={`${ids}-${key}-hint`}
                          className="mt-1 block text-right text-xs font-medium text-status-yellow-fg"
                        >
                          {hint}
                        </span>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Grava a meta "Ano" de cada indicador (empresa), com as regras das metas: mudar ou remover uma meta já
        definida pede justificativa. Em branco = sem meta do ano (vale o acumulado dos períodos, se houver).
      </p>
      {needsReason ? (
        <FormField id={`${ids}-reason`} label="Justificativa (obrigatória)">
          <textarea
            id={`${ids}-reason`}
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={textareaClass}
          />
        </FormField>
      ) : null}
      {error ? <FormAlert tone="error">{errorMessage(error)}</FormAlert> : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          Salvar metas
        </Button>
      </div>
    </form>
  );
}

/**
 * Planejamento › Projeções (T-181b, RF-26, PRD §11.14; DEC-064): a projeção de fechamento do ano de todos os
 * indicadores, com o método e o intervalo usados, a faixa entre os métodos e o farol contra a meta do ano.
 */
export function ProjecoesPage() {
  return (
    <ProjectPage
      title="Projeções"
      description="Fechamento projetado do ano de cada indicador, com o método, os períodos usados e a faixa entre os métodos."
      emptyDescription="As projeções pertencem a um projeto. Quando a empresa tiver um projeto em andamento, elas aparecem aqui."
    >
      {(p) => <ProjectionsSection {...p} />}
    </ProjectPage>
  );
}

function ProjectionsSection({ organizationId, projectId }: { organizationId: string; projectId: string }) {
  const { client } = useAuth();
  const ids = { year: useId(), perspective: useId(), department: useId() };
  // Sem ano escolhido, a API abre no ano corrente, limitado ao horizonte (antes dele, o primeiro ano; depois, o último).
  const [year, setYear] = useState<number | null>(null);
  const [perspectiveId, setPerspectiveId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const project = `/organizations/${organizationId}/projects/${projectId}`;
  const query = useQuery({
    queryKey: ['projections', projectId, year, perspectiveId, departmentId],
    queryFn: ({ signal }) =>
      client.request(`${project}/projections`, {
        query: {
          ...(year ? { year } : {}),
          ...(perspectiveId ? { perspectiveId } : {}),
          ...(departmentId ? { departmentId } : {}),
        },
        schema: projectionsSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const data = query.data;
  const chosenYear = year ?? data?.year ?? '';
  const years = data?.years ?? [];
  return (
    <section aria-labelledby="projections-title" className="mt-6 flex flex-col gap-4">
      <h2 id="projections-title" className="sr-only">
        Projeções de fechamento
      </h2>
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField id={ids.year} label="Ano">
          <NativeSelect id={ids.year} value={chosenYear} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.perspective} label="Perspectiva">
          <NativeSelect
            id={ids.perspective}
            value={perspectiveId}
            onChange={(e) => setPerspectiveId(e.target.value)}
          >
            <option value="">Todas</option>
            {data?.perspectives.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.department} label="Área">
          <NativeSelect
            id={ids.department}
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
          >
            <option value="">Todas</option>
            {data?.departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
      </div>
      {query.isPending ? (
        <LoadingState rows={4} label="Carregando as projeções" />
      ) : query.isError && !data ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : !data || !data.items.length ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={TrendingUp}
          title={
            perspectiveId || departmentId ? 'Nenhum indicador neste filtro' : 'Nenhum indicador no seu escopo'
          }
          description="A projeção sai dos resultados lançados de cada indicador do projeto."
        />
      ) : (
        <>
          {query.isError ? <FormAlert tone="error">{errorMessage(query.error)}</FormAlert> : null}
          <p className="text-sm text-muted-foreground">
            Método: tendência linear dos últimos até 6 períodos com realizado (com 2 ou mais); com um só, a
            média (run-rate). Faixa: o menor e o maior fechamento entre os dois métodos.
          </p>
          <ul aria-label="Projeções por indicador" className="grid gap-3 lg:hidden">
            {data.items.map((item) => (
              <li key={item.kpi.id}>
                <ProjectionCard item={item} />
              </li>
            ))}
          </ul>
          <div
            role="region"
            aria-label="Tabela de projeções"
            tabIndex={0}
            className="relative hidden overflow-x-auto rounded-lg border border-border lg:block"
          >
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Projeção de fechamento de {data.year} por indicador</caption>
              <thead className="bg-muted/60 text-xs text-muted-foreground">
                <tr>
                  {[
                    'Indicador',
                    'Meta do ano',
                    'Realizado',
                    'Fechamento projetado',
                    'Método e intervalo',
                    'Faixa',
                    'Situação',
                  ].map((h, i) => (
                    <th
                      key={h}
                      scope="col"
                      className={`px-3 py-2 font-semibold ${i > 0 && i < 4 ? 'text-right' : 'text-left'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.kpi.id} className="border-t border-border">
                    <th scope="row" className="px-3 py-2 text-left font-semibold">
                      {item.kpi.name}
                      <span className="block text-xs font-normal text-muted-foreground">
                        {item.kpi.code} · {item.kpi.perspectiveName} · {item.kpi.unitLabel}
                      </span>
                    </th>
                    <td className="px-3 py-2 text-right tabular">{value(item.annualTarget)}</td>
                    <td className="px-3 py-2 text-right tabular">
                      {value(item.ytdActual)}
                      {item.lastActualMonth ? (
                        <span className="block text-xs text-muted-foreground">
                          até {MONTHS[item.lastActualMonth - 1]}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular">{value(item.closing)}</td>
                    <td className="px-3 py-2">{methodText(item)}</td>
                    <td className="px-3 py-2 tabular">{rangeText(item)}</td>
                    <td className="px-3 py-2">
                      <Situation item={item} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function methodText(item: ProjectionItem) {
  if (!item.method || !item.basis) return 'Sem realizado no ano: sem projeção';
  const { fromMonth, toMonth, periods } = item.basis;
  const span =
    fromMonth === toMonth ? MONTHS[fromMonth - 1] : `${MONTHS[fromMonth - 1]}–${MONTHS[toMonth - 1]}`;
  return `${PROJECTION_METHOD_LABELS[item.method]} · ${span} (${periods} ${periods === 1 ? 'período' : 'períodos'})`;
}

function rangeText(item: ProjectionItem) {
  if (!item.range) return '—';
  return item.range.low === item.range.high
    ? value(item.range.low)
    : `${value(item.range.low)} a ${value(item.range.high)}`;
}

function Situation({ item }: { item: ProjectionItem }) {
  return (
    <span className="flex flex-col items-start gap-1">
      <StatusBadge status={item.light} />
      {item.closingPerformance !== null ? (
        <span className="text-xs text-muted-foreground">
          {formatDecimal(item.closingPerformance, 1)}% da meta do ano
        </span>
      ) : (
        <span className="sr-only">{TRAFFIC_LIGHT_LABELS[item.light]}</span>
      )}
    </span>
  );
}

function ProjectionCard({ item }: { item: ProjectionItem }) {
  return (
    <article
      aria-label={item.kpi.name}
      className="flex flex-col gap-2 rounded-lg border border-border p-4 text-sm"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold">{item.kpi.name}</h3>
          <p className="text-xs text-muted-foreground">
            {item.kpi.code} · {item.kpi.perspectiveName} · {item.kpi.unitLabel}
          </p>
        </div>
        <Situation item={item} />
      </div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
        <dt className="text-muted-foreground">Meta do ano</dt>
        <dd className="text-right tabular">{value(item.annualTarget)}</dd>
        <dt className="text-muted-foreground">Realizado</dt>
        <dd className="text-right tabular">{value(item.ytdActual)}</dd>
        <dt className="text-muted-foreground">Fechamento projetado</dt>
        <dd className="text-right font-semibold tabular">{value(item.closing)}</dd>
        <dt className="text-muted-foreground">Faixa</dt>
        <dd className="text-right tabular">{rangeText(item)}</dd>
      </dl>
      <p className="text-xs text-muted-foreground">{methodText(item)}</p>
    </article>
  );
}
